import React, { useState, useRef, useEffect } from "react";
import {
  Bell,
  Settings,
  Search,
  X,
  LogOut,
  User,
  Lock,
  UserCog,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { httpRequest } from "../../utils/httpRequest";
import LanguageSwitcher from "../common/LanguageSwitcher";
import { useTranslation } from "react-i18next"; // ← اضافه شده

const Header = () => {
  const { t, i18n } = useTranslation(); // ← اضافه شده
  const [searchOpen, setSearchOpen] = useState(false);
  const [showNotif, setShowNotif] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [user, setUser] = useState(null);

  const notifRef = useRef();
  const settingsRef = useRef();

  const isRtl = i18n.language.includes("fa"); // ← برای تشخیص زبان فارسی

  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem("user"));
    setUser(userData);

    if (userData?.token) {
      fetchNotifications(userData.token);
    }
  }, []);

  const fetchNotifications = async (token) => {
    try {
      const res = await httpRequest(
        "GET",
        "/notification/me",
        {},
        { "x-access-token": token }
      );
      setNotifications(res?.data?.data || []);
    } catch (err) {
      console.error("Failed to fetch notifications", err);
    }
  };

  const handleClickOutside = (e) => {
    if (!notifRef.current?.contains(e.target)) setShowNotif(false);
    if (!settingsRef.current?.contains(e.target)) setShowSettings(false);
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const formatType = (type) => {
    switch (type) {
      case "exam_result":
        return t("header.notification_type_exam_result");
      case "subscription":
        return t("header.notification_type_subscription");
      case "admin_message":
        return t("header.notification_type_admin_message");
      default:
        return t("header.notification_type_default");
    }
  };

  const markAllRead = async () => {
    if (!user?.token) return;
    try {
      await httpRequest(
        "PATCH",
        "/notification/me/read-all",
        {},
        { "x-access-token": user.token }
      );

      // ✅ لوکال update تا سریع UI تغییر کنه
      setTimeout(() => {
        setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
      }, 5000);
    } catch (err) {
      console.error("Failed to mark all read", err);
    }
  };

  const notificationMenuPosition = isRtl ? "right-0" : "left-0";
  const settingsMenuPosition = isRtl ? "right-0" : "left-0";

  const searchInputPlaceholder = t("header.search_placeholder");
  const userFullName = user?.name || t("header.user_placeholder");

  const unreadCount = notifications.filter((n) => !n.isRead).length;

  return (
    <header className="w-full bg-white px-3 md:px-6 py-3 flex items-center justify-between sticky top-0 z-30">
      {/* سرچ موبایل */}
      {searchOpen ? (
        <div className="flex items-center w-full">
          <div className="relative w-full">
            <input
              autoFocus
              type="text"
              placeholder={searchInputPlaceholder}
              className={`pl-10 pr-4 py-2 rounded-lg border text-sm text-gray-700 w-full focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400`}
            />
            <Search
              className={`w-4 h-4 absolute ${
                isRtl ? "left-3" : "right-3"
              } top-1/2 -translate-y-1/2 text-gray-400`}
            />
          </div>
          <button
            className={`${isRtl ? "mr-2" : "ml-2"} p-2`}
            onClick={() => setSearchOpen(false)}
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>
      ) : (
        <>
          {/* ابزار سمت راست (یا چپ) */}
          <div
            className={`flex items-center gap-3 ${
              isRtl ? "flex-row" : "flex-row-reverse"
            }`}
          >
            <button className="md:hidden p-2">
              <Search className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          {/* اعلان‌ها و تنظیمات */}
          <div
            className={`flex items-center gap-3 relative ${
              isRtl ? "flex-row" : "flex-row-reverse"
            }`}
          >
            {/* اعلان‌ها */}
            <div ref={notifRef}>
              <button
                onClick={() => {
                  setShowNotif((prev) => !prev);
                  if (!showNotif) markAllRead(); // وقتی باز میشه
                }}
                className="p-2 rounded-full bg-purple-100 hover:bg-purple-200 relative"
              >
                <Bell className="w-5 h-5 text-purple-700" />
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full ring-2 ring-white" />
                )}
              </button>

              <AnimatePresence>
                {showNotif && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95, y: -10 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.95, y: -10 }}
                    transition={{ duration: 0.2 }}
                    className={`absolute ${notificationMenuPosition} mt-2 w-80 bg-white shadow-lg rounded-lg p-4 z-50 origin-top-right`}
                  >
                    <h4 className="font-bold mb-2 text-sm">
                      {t("header.notifications_title")}
                    </h4>
                    {notifications.length > 0 ? (
                      <ul className="space-y-3 text-sm max-h-64 overflow-y-auto">
                        {notifications.map((notif) => (
                          <li
                            key={notif._id}
                            className={`border-b pb-2 ${
                              notif.isRead
                                ? "opacity-60"
                                : "bg-purple-50 rounded-lg p-2"
                            }`}
                          >
                            <p className="font-semibold text-gray-800">
                              {formatType(notif.type)}
                            </p>
                            <p className="text-gray-600 text-xs">
                              {notif.content}
                            </p>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-gray-500 text-xs">
                        {t("header.no_notifications")}
                      </p>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* تنظیمات کاربر */}
            <div ref={settingsRef}>
              <button
                onClick={() => setShowSettings((prev) => !prev)}
                className="px-2 py-1 rounded-full bg-blue-200 hover:bg-blue-300 flex items-center gap-3"
              >
                <Settings className="w-5 h-5 text-blue-600" />
                <img
                  src={user?.avatar || "/img/user.png"}
                  alt="User"
                  className="w-8 h-8 rounded-full border bg-white"
                />
              </button>

              <AnimatePresence>
                {showSettings && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95, y: -10 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.95, y: -10 }}
                    transition={{ duration: 0.2 }}
                    className={`absolute ${settingsMenuPosition} mt-2 w-72 bg-white shadow-lg rounded-lg p-4 z-50 origin-top-right`}
                  >
                    <div className="flex items-center gap-3 pb-3">
                      <img
                        src={user?.avatar || "/img/user.png"}
                        alt="User"
                        className="w-10 h-10 rounded-full border"
                      />
                      <div>
                        <p className="font-bold">{userFullName}</p>
                        <p className="text-xs text-gray-500">
                          {user?.phone ? user?.phone : user?.email}
                        </p>
                      </div>
                    </div>
                    <div className="space-y-3 text-sm border-t pt-3">
                      {/* <LinkItem icon={<User size={16} />} label={t('header.my_profile')} />
                      <LinkItem icon={<Lock size={16} />} label={t('header.change_password')} /> */}

                      <button
                        className="flex items-center gap-2 text-red-500 text-sm"
                        onClick={() => {
                          localStorage.removeItem("user");
                          window.location.href = "/";
                        }}
                      >
                        <LogOut size={16} />
                        {t("header.logout")}
                      </button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
            <LanguageSwitcher />
          </div>
        </>
      )}
    </header>
  );
};

// این کامپوننت نیازی به تغییر ندارد مگر اینکه از LinkItem استفاده کنید
const LinkItem = ({ icon, label }) => (
  <button className="flex items-center gap-2 text-gray-700 hover:text-indigo-600 transition text-sm">
    {icon}
    {label}
  </button>
);

export default Header;
